/**
 * Zustand store for Device Settings
 * Manages per-device configuration, custom devices, removed/deleted devices.
 * Persists to localStorage for backward compatibility.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { catalog, grades as defaultGrades, type DeviceModel, getDeviceDisplayName } from "@/data/catalog";
import { getOrderedGrades, loadAdminSettings } from "@/stores/adminStore";
import { addAuditEntry } from "@/data/deletionAuditStore";
import { isTenantContext } from "@/lib/tenantScope";

// ─── Constants ─────────────────────────────────────────────────

const CUSTOM_DEVICES_KEY = "khays_custom_devices";
const REMOVED_DEVICES_KEY = "khays_removed_devices";
const DELETED_BUILTIN_DEVICES_KEY = "khays_deleted_builtin_devices";
const DEVICE_SETTINGS_KEY = "khays_device_settings";
const DEVICE_RENAMES_KEY = "khays_device_renames";
const EXTRA_VARIANTS_KEY = "khays_extra_variants";
const VARIANT_OVERRIDES_KEY = "khays_variant_overrides";
const SUB_VARIANT_DEVICE_KEY = "khays_sub_variant_device_settings";

// ─── Types ─────────────────────────────────────────────────────

export interface DeviceColor {
  id: string;
  name: string;
  hex: string;
}

export interface DeviceSettings {
  enabled: boolean;
  sortOrder: number;
  enabledGrades: string[];
  subconditionsEnabled: boolean;
  colors: DeviceColor[];
  enquiryOnly: boolean;
  enquiryOnlyVariants: Record<string, boolean>;
  disabledVariants?: string[];
  /**
   * Lite theme — device-level fallback discount % (1–95).
   * Used when no flash sale is active *and* no per-key override exists.
   * 0 / undefined → no badge.
   */
  discountPercent?: number;
  /**
   * Lite theme — per-priceKey discount overrides.
   * Key shape mirrors getPriceKey output (`name|variant|grade[|screen][|sub]`)
   * and wins over `discountPercent` when present.
   * Empty / undefined map → fall back to device-level discount.
   */
  discountPctOverrides?: Record<string, number>;
  /**
   * Lite card price-resolution mode (per device):
   *   • "auto-low"   → cheapest combo (default)
   *   • "auto-high"  → highest-grade combo
   *   • "admin"      → use cardPinnedCombos[variantLabel] (falls back to auto-low if missing)
   *   • "system"     → respects pricing menu base (skips per-key discount; uses backend default)
   */
  cardPriceMode?: "auto-low" | "auto-high" | "admin" | "system";
  /**
   * Lite card admin-pinned combo per variant (`variantLabel` → suffix `grade|screen|sub` or `grade`).
   * Used when cardPriceMode === "admin".
   * Suffix shape: "GradeA+|Original Screen|WiFi" — empty parts are dropped.
   */
  cardPinnedCombos?: Record<string, { grade?: string; screen?: string; sub?: string }>;
}

// ─── Helpers ───────────────────────────────────────────────────

function loadJsonValue<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch {}
  return fallback;
}

function saveJsonValue(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getDefaultDeviceSettings(category?: string): DeviceSettings {
  // Use the ordered grades (respects deletions/custom grades) instead of hardcoded defaults.
  // getOrderedGrades is internally memoized, so this is cheap on hot paths.
  const orderedGrades = getOrderedGrades(false);
  let grades = orderedGrades.length > 0 ? orderedGrades : [...defaultGrades];

  // Filter grades by category assignment if applicable
  if (category) {
    const adminSettings = loadAdminSettings();
    const gradeCategories: Record<string, string[]> = adminSettings.gradeCategories || {};
    grades = grades.filter(g => {
      const cats = gradeCategories[g];
      if (!cats || cats.length === 0) return true;
      return cats.includes(category);
    });
  }

  return {
    enabled: true,
    sortOrder: 0,
    enabledGrades: grades,
    subconditionsEnabled: true,
    colors: [],
    enquiryOnly: false,
    enquiryOnlyVariants: {},
  };
}

// ─── State shape ───────────────────────────────────────────────

interface DeviceSettingsStoreState {
  allSettings: Record<string, DeviceSettings>;
  customDevices: DeviceModel[];
  removedDevices: string[];
  deletedBuiltinDevices: string[];
  /** revision counter — bumped on every write so selectors re-derive */
  _rev: number;

  // Actions
  setDeviceSettings: (deviceName: string, settings: DeviceSettings) => void;
  resetDeviceSettings: (deviceName: string) => void;
  saveAllDeviceSettings: (settings: Record<string, DeviceSettings>) => void;
  setCustomDevices: (devices: DeviceModel[]) => void;
  setRemovedDevices: (names: string[]) => void;
  setDeletedBuiltinDevices: (names: string[]) => void;
  reload: () => void;
}

function loadState() {
  return {
    allSettings: loadJsonValue<Record<string, DeviceSettings>>(DEVICE_SETTINGS_KEY, {}),
    customDevices: loadJsonValue<DeviceModel[]>(CUSTOM_DEVICES_KEY, []),
    removedDevices: loadJsonValue<string[]>(REMOVED_DEVICES_KEY, []),
    deletedBuiltinDevices: loadJsonValue<string[]>(DELETED_BUILTIN_DEVICES_KEY, []),
  };
}

export const useDeviceSettingsStore = create<DeviceSettingsStoreState>()(
  devtools(
    (set, get) => ({
      ...loadState(),
      _rev: 0,

  setDeviceSettings: (deviceName, settings) => {
    const all = { ...get().allSettings, [deviceName]: settings };
    localStorage.setItem(DEVICE_SETTINGS_KEY, JSON.stringify(all));
    set({ allSettings: all, _rev: get()._rev + 1 });
  },

  resetDeviceSettings: (deviceName) => {
    const all = { ...get().allSettings };
    delete all[deviceName];
    localStorage.setItem(DEVICE_SETTINGS_KEY, JSON.stringify(all));
    set({ allSettings: all, _rev: get()._rev + 1 });
  },

  saveAllDeviceSettings: (settings) => {
    localStorage.setItem(DEVICE_SETTINGS_KEY, JSON.stringify(settings));
    set({ allSettings: settings, _rev: get()._rev + 1 });
  },

  setCustomDevices: (devices) => {
    localStorage.setItem(CUSTOM_DEVICES_KEY, JSON.stringify(devices));
    set({ customDevices: devices, _rev: get()._rev + 1 });
  },

  setRemovedDevices: (names) => {
    localStorage.setItem(REMOVED_DEVICES_KEY, JSON.stringify(names));
    set({ removedDevices: names, _rev: get()._rev + 1 });
  },

  setDeletedBuiltinDevices: (names) => {
    localStorage.setItem(DELETED_BUILTIN_DEVICES_KEY, JSON.stringify(names));
    set({ deletedBuiltinDevices: names, _rev: get()._rev + 1 });
  },

  reload: () => {
    set({ ...loadState(), _rev: get()._rev + 1 });
  },
    }),
    { name: "DeviceSettingsStore" }
  )
);

export function useDeviceSettingsRevision(): number {
  return useDeviceSettingsStore(state => state._rev);
}

// ─── Backward-compatible exports ──────────────────────────────

export function getActiveDevices(): DeviceModel[] {
  const { customDevices, removedDevices, deletedBuiltinDevices, allSettings } = useDeviceSettingsStore.getState();
  const customNames = new Set(customDevices.map(d => d.name));
  const result: DeviceModel[] = [];

  // Tenants start empty — built-in platform catalog is hidden until the
  // Store Manager imports the starter catalog or adds devices manually.
  const tenantScoped = isTenantContext();
  if (!tenantScoped) {
    for (const d of catalog) {
      if (customNames.has(d.name)) continue;
      if (removedDevices.includes(d.name)) continue;
      if (deletedBuiltinDevices.includes(d.name)) continue;
      result.push(d);
    }
  }

  for (const d of customDevices) {
    const isOverride = !tenantScoped && catalog.some(c => c.name === d.name);
    if (deletedBuiltinDevices.includes(d.name)) continue;
    if (!isOverride && removedDevices.includes(d.name)) continue;
    result.push(d);
  }

  // Sort by device management sort order
  result.sort((a, b) => {
    const aOrder = allSettings[a.name]?.sortOrder ?? 0;
    const bOrder = allSettings[b.name]?.sortOrder ?? 0;
    return aOrder - bOrder;
  });

  return result;
}

export function getDeviceSettings(deviceName: string): DeviceSettings {
  const all = useDeviceSettingsStore.getState().allSettings;
  const defaults = getDefaultDeviceSettings();
  const stored = all[deviceName];
  if (!stored) return defaults;
  return { ...defaults, ...stored, enquiryOnlyVariants: stored.enquiryOnlyVariants || {} };
}

export function getAllDeviceSettings(): Record<string, DeviceSettings> {
  return useDeviceSettingsStore.getState().allSettings;
}

export function setDeviceSettings(deviceName: string, settings: DeviceSettings): void {
  useDeviceSettingsStore.getState().setDeviceSettings(deviceName, settings);
}

export function resetDeviceSettings(deviceName: string): void {
  useDeviceSettingsStore.getState().resetDeviceSettings(deviceName);
}

export function getSortedDevicesForCategory(devices: DeviceModel[], category: string): DeviceModel[] {
  return devices
    .filter(d => d.category === category && getDeviceSettings(d.name).enabled)
    .sort((a, b) => {
      const sa = getDeviceSettings(a.name).sortOrder || 0;
      const sb = getDeviceSettings(b.name).sortOrder || 0;
      return sa - sb;
    });
}

export function isVariantEnabled(deviceName: string, variantLabel: string): boolean {
  const s = getDeviceSettings(deviceName);
  if (!s.disabledVariants || s.disabledVariants.length === 0) return true;
  return !s.disabledVariants.includes(variantLabel);
}

export function setVariantDisabled(deviceName: string, variantLabel: string, disabled: boolean): void {
  const s = getDeviceSettings(deviceName);
  if (!s.disabledVariants) s.disabledVariants = [];
  if (disabled) {
    if (!s.disabledVariants.includes(variantLabel)) s.disabledVariants.push(variantLabel);
  } else {
    s.disabledVariants = s.disabledVariants.filter(v => v !== variantLabel);
  }
  setDeviceSettings(deviceName, s);
}

export function setDeviceEnabled(deviceName: string, enabled: boolean): void {
  const s = getDeviceSettings(deviceName);
  s.enabled = enabled;
  setDeviceSettings(deviceName, s);
}

export function setDeviceSortOrder(deviceName: string, order: number): void {
  const s = getDeviceSettings(deviceName);
  s.sortOrder = order;
  setDeviceSettings(deviceName, s);
}

export function setDeviceGrades(deviceName: string, grades: string[]): void {
  const s = getDeviceSettings(deviceName);
  s.enabledGrades = grades;
  setDeviceSettings(deviceName, s);
}

export function setDeviceSubconditions(deviceName: string, enabled: boolean): void {
  const s = getDeviceSettings(deviceName);
  s.subconditionsEnabled = enabled;
  setDeviceSettings(deviceName, s);
}

export function setDeviceEnquiryOnly(deviceName: string, enquiryOnly: boolean): void {
  const s = getDeviceSettings(deviceName);
  s.enquiryOnly = enquiryOnly;
  setDeviceSettings(deviceName, s);
}

export function setVariantEnquiryOnly(deviceName: string, variantLabel: string, enquiryOnly: boolean): void {
  const s = getDeviceSettings(deviceName);
  if (!s.enquiryOnlyVariants) s.enquiryOnlyVariants = {};
  if (enquiryOnly) s.enquiryOnlyVariants[variantLabel] = true;
  else delete s.enquiryOnlyVariants[variantLabel];
  setDeviceSettings(deviceName, s);
}

export function isVariantEnquiryOnly(deviceName: string, variantLabel: string): boolean {
  const s = getDeviceSettings(deviceName);
  if (s.enquiryOnly) return true;
  return !!(s.enquiryOnlyVariants && s.enquiryOnlyVariants[variantLabel]);
}

export function addDeviceColor(deviceName: string, color: DeviceColor): void {
  const s = getDeviceSettings(deviceName);
  s.colors = [...s.colors, color];
  setDeviceSettings(deviceName, s);
}

export function removeDeviceColor(deviceName: string, colorId: string): void {
  const s = getDeviceSettings(deviceName);
  s.colors = s.colors.filter(c => c.id !== colorId);
  setDeviceSettings(deviceName, s);
}

export function updateDeviceColor(deviceName: string, colorId: string, updates: Partial<DeviceColor>): void {
  const s = getDeviceSettings(deviceName);
  s.colors = s.colors.map(c => c.id === colorId ? { ...c, ...updates } : c);
  setDeviceSettings(deviceName, s);
}

export function addDeviceColors(deviceName: string, colors: DeviceColor[]): void {
  const s = getDeviceSettings(deviceName);
  const existing = new Set(s.colors.map(c => c.name));
  const newColors = colors.filter(c => !existing.has(c.name)).map(c => ({
    ...c,
    id: `color_${Date.now()}_${Math.random().toString(36).slice(2, 6)}_${c.name.replace(/\s/g, '')}`,
  }));
  s.colors = [...s.colors, ...newColors];
  setDeviceSettings(deviceName, s);
}

export function bulkSetSubconditions(deviceNames: string[], enabled: boolean): void {
  const store = useDeviceSettingsStore.getState();
  const all = { ...store.allSettings };
  deviceNames.forEach(name => {
    if (!all[name]) all[name] = getDefaultDeviceSettings();
    all[name] = { ...all[name], subconditionsEnabled: enabled };
  });
  store.saveAllDeviceSettings(all);
}

export function bulkSetGrades(deviceNames: string[], grades: string[]): void {
  const store = useDeviceSettingsStore.getState();
  const all = { ...store.allSettings };
  deviceNames.forEach(name => {
    if (!all[name]) all[name] = getDefaultDeviceSettings();
    all[name] = { ...all[name], enabledGrades: [...grades] };
  });
  store.saveAllDeviceSettings(all);
}

export function bulkAddColor(deviceNames: string[], color: DeviceColor): void {
  const store = useDeviceSettingsStore.getState();
  const all = { ...store.allSettings };
  deviceNames.forEach(name => {
    if (!all[name]) all[name] = getDefaultDeviceSettings();
    if (!all[name].colors.find(c => c.name === color.name)) {
      all[name] = { ...all[name], colors: [...all[name].colors, { ...color, id: `color_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }] };
    }
  });
  store.saveAllDeviceSettings(all);
}

// ─── Removed / Deleted devices ────────────────────────────────

export function loadRemovedDevices(): string[] {
  return useDeviceSettingsStore.getState().removedDevices;
}

export function saveRemovedDevices(names: string[]): void {
  useDeviceSettingsStore.getState().setRemovedDevices(names);
}

export function loadDeletedBuiltinDevices(): string[] {
  return useDeviceSettingsStore.getState().deletedBuiltinDevices;
}

export function saveDeletedBuiltinDevices(names: string[]): void {
  useDeviceSettingsStore.getState().setDeletedBuiltinDevices(names);
}

export function removeDevice(deviceName: string): void {
  const removed = [...loadRemovedDevices()];
  if (!removed.includes(deviceName)) {
    removed.push(deviceName);
    saveRemovedDevices(removed);
  }
}

export function restoreDevice(deviceName: string): void {
  saveRemovedDevices(loadRemovedDevices().filter(n => n !== deviceName));
}

export function isDeviceRemoved(deviceName: string): boolean {
  return loadRemovedDevices().includes(deviceName);
}

export function isDeviceHardDeleted(deviceName: string): boolean {
  return loadDeletedBuiltinDevices().includes(deviceName);
}

// ─── Custom devices ───────────────────────────────────────────

function loadCustomDevicesInternal(): DeviceModel[] {
  return useDeviceSettingsStore.getState().customDevices;
}

function saveCustomDevicesInternal(devices: DeviceModel[]): void {
  useDeviceSettingsStore.getState().setCustomDevices(devices);
}

// ─── Permanent deletion ───────────────────────────────────────

function removeDeviceReferences(deviceName: string): string[] {
  const cleanedUp: string[] = [];
  const store = useDeviceSettingsStore.getState();

  // Device settings
  const allSettings = { ...store.allSettings };
  if (deviceName in allSettings) {
    delete allSettings[deviceName];
    store.saveAllDeviceSettings(allSettings);
    cleanedUp.push("Device settings (grades, colors, subconditions)");
  }

  const deviceRenames = loadJsonValue<Record<string, string>>(DEVICE_RENAMES_KEY, {});
  if (deviceName in deviceRenames) {
    delete deviceRenames[deviceName];
    saveJsonValue(DEVICE_RENAMES_KEY, deviceRenames);
    cleanedUp.push("Display name override");
  }

  const extraVariants = loadJsonValue<Record<string, unknown[]>>(EXTRA_VARIANTS_KEY, {});
  if (deviceName in extraVariants) {
    delete extraVariants[deviceName];
    saveJsonValue(EXTRA_VARIANTS_KEY, extraVariants);
    cleanedUp.push("Extra variants");
  }

  const variantOverrides = loadJsonValue<Record<string, Record<string, unknown>>>(VARIANT_OVERRIDES_KEY, {});
  if (deviceName in variantOverrides) {
    delete variantOverrides[deviceName];
    saveJsonValue(VARIANT_OVERRIDES_KEY, variantOverrides);
    cleanedUp.push("Variant overrides");
  }

  const subVariantDeviceSettings = loadJsonValue<Record<string, Record<string, boolean>>>(SUB_VARIANT_DEVICE_KEY, {});
  let touchedSubVariantSettings = false;
  Object.values(subVariantDeviceSettings).forEach(deviceMap => {
    if (deviceMap && deviceName in deviceMap) {
      delete deviceMap[deviceName];
      touchedSubVariantSettings = true;
    }
  });
  if (touchedSubVariantSettings) {
    saveJsonValue(SUB_VARIANT_DEVICE_KEY, subVariantDeviceSettings);
    cleanedUp.push("Sub-variant device settings");
  }

  try {
    const links = loadJsonValue<Array<{ id: string; deviceName: string }>>("khays_device_links", []);
    const filtered = links.filter(l => l.deviceName !== deviceName);
    if (filtered.length < links.length) {
      saveJsonValue("khays_device_links", filtered);
      cleanedUp.push(`Device links (${links.length - filtered.length} removed)`);
    }
  } catch {}

  try {
    const modes = loadJsonValue<Record<string, string>>("khays_device_subcondition_modes", {});
    if (deviceName in modes) {
      delete modes[deviceName];
      saveJsonValue("khays_device_subcondition_modes", modes);
      cleanedUp.push("Screen subcondition mode override");
    }
  } catch {}

  try {
    const adminSettings = loadJsonValue<Record<string, any>>("khays_admin_settings", {});
    let touched = false;
    for (const settingKey of ["priceOverrides", "deviceExtraProfits", "deviceMargins", "deviceCommissionRates"]) {
      const obj = adminSettings[settingKey];
      if (obj && typeof obj === "object") {
        const keysToDelete = Object.keys(obj).filter(k => k.startsWith(deviceName + "|") || k === deviceName);
        if (keysToDelete.length > 0) {
          keysToDelete.forEach(k => delete obj[k]);
          touched = true;
          cleanedUp.push(`${settingKey} (${keysToDelete.length} entries)`);
        }
      }
    }
    if (touched) saveJsonValue("khays_admin_settings", adminSettings);
  } catch {}

  try {
    const stock = loadJsonValue<Record<string, any>>("khays_stock", {});
    const stockKeys = Object.keys(stock).filter(k => k.startsWith(deviceName + "|"));
    if (stockKeys.length > 0) {
      stockKeys.forEach(k => delete stock[k]);
      saveJsonValue("khays_stock", stock);
      cleanedUp.push(`Stock entries (${stockKeys.length} removed)`);
    }
  } catch {}

  try {
    const media = loadJsonValue<Record<string, any>>("khays_media", {});
    const mediaKeys = Object.keys(media).filter(k => k.startsWith(deviceName + "|") || k.includes(`|${deviceName}|`));
    if (mediaKeys.length > 0) {
      mediaKeys.forEach(k => delete media[k]);
      saveJsonValue("khays_media", media);
      cleanedUp.push(`Media entries (${mediaKeys.length} removed)`);
    }
  } catch {}

  saveCustomDevicesInternal(loadCustomDevicesInternal().filter(device => device.name !== deviceName));
  saveRemovedDevices(loadRemovedDevices().filter(name => name !== deviceName));
  cleanedUp.push("Removed from custom devices and removed list");

  return cleanedUp;
}

export function permanentlyDeleteDevice(deviceName: string): string[] {
  const cleanedUp = removeDeviceReferences(deviceName);

  if (catalog.some(device => device.name === deviceName)) {
    const deleted = new Set(loadDeletedBuiltinDevices());
    deleted.add(deviceName);
    saveDeletedBuiltinDevices(Array.from(deleted));
    cleanedUp.push("Added to hard-deleted built-in devices");
  }

  addAuditEntry({ itemType: "device", itemName: deviceName, cleanedUp });
  return cleanedUp;
}

export function permanentlyDeleteDevices(deviceNames: string[]): void {
  Array.from(new Set(deviceNames)).forEach(permanentlyDeleteDevice);
}
